<script>
import range from 'lodash/range'
import roundTime from '@utils/round-time'
import { isToday } from 'date-fns'

const timeToString = (number = 0) => {
  return number < 10 ? `0${number}` : number.toString()
}
const optionMapper = (number) => {
  return { value: timeToString(number), text: timeToString(number) }
}

export default {
  props: {
    value: {
      type: Date,
      default: () => new Date(),
    },

    required: {
      type: Boolean,
      default: false,
    },

    minutesDelay: {
      type: Boolean,
      default: false,
    },

    disabledTime: {
      type: Date,
      default: () => new Date(0),
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    minutesRange: {
      type: Number,
      default: 5,
    },

    disabledMinutesBuffer: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['input'],

  computed: {
    sameSelectedDates() {
      return (
        this.hourlessDates(this.value) === this.hourlessDates(this.disabledTime)
      )
    },

    areHoursEqual() {
      return this.value.getHours() === this.disabledTime?.getHours()
    },

    isSelectedHourNow() {
      let now = new Date().getHours().toString()
      if (now < 10) {
        now = ('0' + now).slice(-2)
      }
      return this.selectedHour !== null && this.selectedHour === now
    },

    isEffectiveTo() {
      return this.disabledTime?.getTime() !== new Date(0).getTime()
    },

    computeDisabledForToday() {
      if (this.isEffectiveTo) {
        return range(0, 24).map((nb) => {
          return this.disabledHoursMapper(nb, this.disableEarlierHours)
        })
      }
      return range(0, 24).map((nb) => {
        return this.disabledHoursMapper(nb, this.disableTodaysPastHours)
      })
    },

    computeDisabledForOtherDays() {
      if (this.isEffectiveTo) {
        if (this.sameSelectedDates) {
          return range(0, 24).map((nb) => {
            return this.disabledHoursMapper(nb, this.disableEarlierHours)
          })
        }
      }
      return range(0, 24).map(optionMapper)
    },

    hours() {
      if (this.minutesDelay) {
        if (isToday(this.value) && this.sameSelectedDates) {
          return this.computeDisabledForToday
        } else {
          return this.computeDisabledForOtherDays
        }
      }
      return range(0, 24).map(optionMapper)
    },

    computeDisabledTodayNow() {
      if (this.isEffectiveTo) {
        return range(0, 60, this.minutesRange).map((nb) => {
          return this.disabledMinutesMapper(nb, this.disableEarlierMinutes)
        })
      }
      return range(0, 60, this.minutesRange).map((nb) => {
        return this.disabledMinutesMapper(
          nb,
          this.disableCurrentHoursPastMinutes
        )
      })
    },

    computeDisabledTimeOtherwise() {
      if (this.isEffectiveTo) {
        if (this.sameSelectedDates) {
          if (!this.areHoursEqual) {
            return range(0, 60, this.minutesRange).map(optionMapper)
          }
          return range(0, 60, this.minutesRange).map((nb) => {
            return this.disabledMinutesMapper(nb, this.disableEarlierMinutes)
          })
        }
      }
      return range(0, 60, this.minutesRange).map(optionMapper)
    },

    minutes() {
      if (this.minutesDelay) {
        if (isToday(this.value) && this.isSelectedHourNow) {
          return this.computeDisabledTodayNow
        } else {
          return this.computeDisabledTimeOtherwise
        }
      }
      return range(0, 60, this.minutesRange).map(optionMapper)
    },

    selectedHour() {
      return timeToString(this.value?.getHours() || 0)
    },

    selectedMinute() {
      return timeToString(roundTime(this.value)?.getMinutes() || 0)
    },
  },

  methods: {
    disableEarlierHours(number) {
      return number < this.disabledTime?.getHours().toString()
    },

    disableTodaysPastHours(number) {
      if (this.minutesDelay) {
        return number < new Date().getHours()
      }
      return false
    },

    disabledHoursMapper(number, getDisabledVals) {
      return {
        value: timeToString(number),
        text: timeToString(number),
        disabled: getDisabledVals(number),
      }
    },

    disableEarlierMinutes(number) {
      const minutesBuffer = this.disabledMinutesBuffer ? this.minutesRange : 0

      return number < this.disabledTime?.getMinutes() + minutesBuffer
    },

    disableCurrentHoursPastMinutes(number) {
      if (this.minutesDelay) {
        return number < new Date().getMinutes()
      }
      return false
    },

    disabledMinutesMapper(number, getDisabledVals) {
      return {
        value: timeToString(number),
        text: timeToString(number),
        disabled: getDisabledVals(number),
      }
    },

    hourlessDates(date) {
      return new Date(date).setHours(0, 0, 0, 0)
    },

    updateSelectedHour(newValue) {
      const date = this.value ? this.value : new Date()
      this.$emit('input', new Date(date.setHours(newValue)))
    },

    updateSelectedMinute(newValue) {
      const date = this.value ? this.value : new Date()
      this.$emit('input', new Date(date.setMinutes(newValue)))
    },
  },
}
</script>

<template>
  <div class="form-row">
    <div class="col">
      <b-form-select
        :options="hours"
        :value="selectedHour"
        :required="required"
        :disabled="disabled"
        @input="updateSelectedHour"
      />
    </div>
    <div class="col">
      <b-form-select
        :options="minutes"
        :value="selectedMinute"
        :required="required"
        :disabled="disabled"
        @input="updateSelectedMinute"
      />
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@variables';
</style>
